import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactMessage, setContactMessage] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setContactStatus('success');
      setContactMessage('消息已发送！');
      setContactForm({ name: '', email: '', message: '' });
      return;
    }

    if (contactForm.message.length > 500) {
      setContactStatus('error');
      setContactMessage('消息内容不能超过500个字符。');
      return;
    }

    setContactStatus('loading');
    setContactMessage('');

    try {
      const payload = new URLSearchParams();
      payload.append('name', contactForm.name);
      payload.append('email', contactForm.email);
      payload.append('message', contactForm.message);
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          payload.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9qmdgsnlsngrm4lm220', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactMessage('感谢你的留言！我们会尽快与你联系。');
        setContactForm({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setContactStatus('error');
          setContactMessage('提交失败，请稍后重试。');
        } else {
          setContactStatus('error');
          setContactMessage(serverMsg || '提交失败，请稍后重试。');
        }
      }
    } catch {
      setContactStatus('error');
      setContactMessage('网络错误，请检查连接后重试。');
    }
  };

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于耳骚 - 极致的听觉体验平台',
    description: '耳骚是一个专注于ASMR听觉体验的中文内容平台，致力于为用户提供最优质的ASMR音频、沉浸式声景体验和高品质耳机推荐。',
    url: 'https://ersao.fun/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '耳骚',
      url: 'https://ersao.fun/',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '耳骚',
    url: 'https://ersao.fun/',
    description: '专注于ASMR听觉体验的中文内容平台',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@ersao.fun',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <Navbar />
      <main>
        <section className="relative w-full h-[280px] md:h-[380px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20serene%20minimalist%20workspace%20with%20headphones%20and%20a%20notebook%20on%20a%20wooden%20desk%2C%20soft%20warm%20natural%20lighting%2C%20cream%20walls%2C%20peaceful%20creative%20atmosphere%2C%20editorial%20photography%20style%2C%20no%20text&width=1800&height=700&seq=about-hero&orientation=landscape"
            alt="关于耳骚"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-3">
              关于耳骚
            </h1>
            <p className="text-sm text-background-200/80 max-w-lg mx-auto leading-relaxed">
              让每一双耳朵，都找到属于自己的声音天堂
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />
            </div>
          </div>
        </section>

        <section className="pb-14 md:pb-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-16">
              <div className="lg:col-span-2">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20cozy%20listening%20room%20with%20a%20comfortable%20chair%20and%20premium%20headphones%2C%20soft%20warm%20lighting%20from%20a%20window%2C%20cream%20walls%2C%20wooden%20floor%2C%20peaceful%20atmosphere%2C%20editorial%20interior%20photography%2C%20vertical%20composition&width=600&height=800&seq=about-brand&orientation=portrait"
                  alt="耳骚品牌形象"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
              <div className="lg:col-span-3 flex flex-col justify-center">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    耳骚诞生于一个简单的想法：<strong>让更多人感受到声音的极致魅力</strong>。在这个信息喧嚣的时代，我们常常忽略了耳朵最原始的渴望——那些能让人头皮发麻、身心放松的美妙声音。
                  </p>
                  <p>
                    我们的团队由ASMR资深爱好者、音频工程师、耳机评测师和心理咨询师组成。大家从各自的专业领域出发，共同打造这个专注于听觉体验的内容平台。
                  </p>
                  <p>
                    从ASMR音频的精选推荐，到沉浸式自然声景的构建，再到高品质耳机的专业评测——耳骚的目标只有一个：<strong>让你的耳朵体验到前所未有的满足感</strong>。
                  </p>
                  <p>
                    "耳骚"这个名字，取自那种被声音撩动心弦、让耳朵"骚动不安"的极致感受。我们希望每一个来到耳骚的人，都能找到那份属于自己的听觉天堂。
                  </p>
                </div>
              </div>
            </div>

            <div id="reputation" className="mb-16">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  {
                    quote: '自从关注了耳骚，我的睡眠质量提升了好几个档次。雨声和白噪音推荐太实用了！',
                    author: '小雅',
                    role: '失眠改善者',
                  },
                  {
                    quote: '耳骚的耳机评测帮我在茫茫耳机海中找到了最适合的那一副。现在听ASMR真的是一种享受。',
                    author: '耳机控阿杰',
                    role: '音频爱好者',
                  },
                  {
                    quote: '我一直以为自己感受不到ASMR，直到按照耳骚的敏感度训练指南尝试了一周后，终于体验到那种酥麻的快感！',
                    author: '声音探险家',
                    role: 'ASMR新手',
                  },
                  {
                    quote: '自然声景系列简直是宝藏！在办公室里戴上耳机，瞬间穿越到冰岛冰川，压力全没了。',
                    author: '林小姐',
                    role: '都市白领',
                  },
                  {
                    quote: '作为一个ASMR创作者，耳骚的技术科普帮我提升了很多录音质量。双耳录音那篇文章读了不下五遍。',
                    author: '小耳朵电台',
                    role: 'ASMR创作者',
                  },
                  {
                    quote: '推荐给了身边很多焦虑的朋友，声音疗愈真的有效。感谢耳骚提供了这么好的内容平台。',
                    author: '心理咨询师大刘',
                    role: '心理健康从业者',
                  },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                      &ldquo;{item.quote}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary-600">
                          {item.author[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{item.author}</p>
                        <p className="text-xs text-foreground-500">{item.role}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div id="contact" className="bg-background-100 rounded-lg p-6 md:p-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-4">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    有任何问题、建议或合作意向？欢迎通过以下方式联系我们，或填写表单，我们会尽快回复你。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">邮箱</p>
                        <p className="text-sm font-medium text-foreground-900">hello@ersao.fun</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100">
                        <i className="ri-wechat-line text-accent-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">微信公众号</p>
                        <p className="text-sm font-medium text-foreground-900">耳骚ErSao</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100">
                        <i className="ri-bilibili-line text-secondary-500"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">B站</p>
                        <p className="text-sm font-medium text-foreground-900">耳骚官方</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <form
                    onSubmit={handleContactSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1">
                        留言
                        <span className="text-foreground-400 font-normal ml-1">
                          ({contactForm.message.length}/500)
                        </span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={contactStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 whitespace-nowrap cursor-pointer"
                    >
                      {contactStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                    {contactMessage && (
                      <p
                        className={`text-sm ${
                          contactStatus === 'success' ? 'text-accent-600' : 'text-red-500'
                        }`}
                      >
                        {contactMessage}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}